import React, { useEffect, useState } from "react";
import Card from "components/card";
import { MdCancel, MdCheckCircle, MdOutlineTipsAndUpdates } from "react-icons/md";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { subscriptionActivity } from "services/apiService";

type RowObj = {
  name: string;
  status: string;
  date: string;
  end_date: string | null;
};

const columnHelper = createColumnHelper<RowObj>();

export default function SubscriptionTable() {
  const [data, setData] = useState<RowObj[]>([]);
  const [sorting, setSorting] = useState<SortingState>([]);

  const columns = [
    columnHelper.accessor("name", {
      id: "name",
      header: () => (
        <p className="text-xs font-semibold text-[#6B7280] uppercase tracking-wide">Plan Name</p>
      ),
      cell: (info) => (
        <p className="text-sm font-medium text-[#1F2937] dark:text-white">
          {info.getValue()}
        </p>
      ),
    }),
    columnHelper.accessor("status", {
      id: "status",
      header: () => (
        <p className="text-xs font-semibold text-[#6B7280] uppercase tracking-wide">Status</p>
      ),
      cell: (info) => (
        <div className="flex items-center gap-1">
          {info.getValue() === "Active" ? (
            <MdCheckCircle className="text-brand-500 shrink-0" />
          ) : info.getValue() === "Canceled" ? (
            <MdCancel className="text-[#DC2626] shrink-0" />
          ) : info.getValue() === "Upgraded" ? (
            <MdOutlineTipsAndUpdates className="text-[#D97706] shrink-0" />
          ) : null}
          <p className="text-sm font-medium text-[#1F2937] dark:text-white">
            {info.getValue()}
          </p>
        </div>
      ),
    }),
    columnHelper.accessor("date", {
      id: "date",
      header: () => (
        <p className="text-xs font-semibold text-[#6B7280] uppercase tracking-wide">Start Date</p>
      ),
      cell: (info) => (
        <p className="text-sm text-[#1F2937] dark:text-white">
          {info.getValue()}
        </p>
      ),
    }),
    columnHelper.accessor("end_date", {
      id: "end_date",
      header: () => (
        <p className="text-xs font-semibold text-[#6B7280] uppercase tracking-wide">End Date</p>
      ),
      cell: (info) => (
        <p className="text-sm text-[#6B7280] dark:text-white">
          {info.getValue() ?? "—"}
        </p>
      ),
    }),
  ];

  // Fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const responseData = await subscriptionActivity();
        const formattedData = responseData.data.map((item: any) => ({
          name: item.plan_name,
          status: item.upgrade_status
            ? "Upgraded"
            : item.status
            ? "Active"
            : "Canceled",
          date: new Date(item.start_date).toLocaleString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
          }),
          end_date: item.end_date ? new Date(item.end_date).toLocaleString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
          }) : null,
        }));
        setData(formattedData);
      } catch (error) {
        console.error("Error fetching subscription activity:", error);
      }
    };

    fetchData();
  }, []);

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: true,
  });

  return (
    <Card extra={"w-full h-full px-6 pb-6"}>
      <div className="relative flex items-center justify-between pt-4">
        <h4 className="text-base font-semibold text-[#1F2937] dark:text-white">
          Subscription Activity
        </h4>
      </div>

      <div className="mt-8 max-h-[400px] overflow-y-auto"> {/* Set a max height for scrolling */}
        <table className="w-full table-auto"> {/* Added table-auto to manage width */}
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="!border-px !border-gray-400">
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    colSpan={header.colSpan}
                    onClick={header.column.getToggleSortingHandler()}
                    className="cursor-pointer border-b-[1px] border-gray-200 pt-4 pb-2 pr-4 text-start"
                  >
                    <div className="items-center justify-between text-xs text-gray-200">
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                      {header.column.getIsSorted() ? (
                        <span>{header.column.getIsSorted() === 'asc' ? ' 🔼' : ' 🔽'}</span>
                      ) : null}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className="min-w-[150px] border-white/0 py-3 pr-4"
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}